import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { FREE_TRIAL_ENABLED, STORE_PAYMENT_MODAL_TYPE } from '../../const';
import PaymentApi from '../../service/Api/paymentApi';
import { getUserPlan } from './authSlice';
import { getFeaturedSubscriptionPlan, setSubscriptionPlan } from './paymentsSlice';

const initialState = {
    showPaymentModal: false,
    modalType: '',
    loading: false,
    error: null,
    // HORO-10504: Zara upsell modal — global/redux-controlled like
    // showPaymentModal, rather than component-local state, so any future
    // trigger (email entry, once-per-launch-day announcement) can open it
    // with the same dispatch regardless of where that logic lives.
    showZaraUpsellModal: false,
    // HORO-10504: one-time "first time in Zara" welcome screen for premium
    // subscribers — same global/redux pattern, since it's opened from the
    // same two entry points (catalog CTA, nav menu) as the upsell modal.
    showZaraWelcomeModal: false,
    // HORO-10504: which advisor's upsell (if any) opened the payment modal —
    // this modal is shared by every subscribe entry point (message-limit
    // paywall, tarot paywall, issue retry, profile upgrade, Zara), so a
    // logged-in Zara subscriber has no other way to tell the shared
    // PlanPaymentContent/PaymentButton "tag the eventual checkout attempt
    // as mine" (see useGoToCheckout, which reads this as a fallback when
    // paymentData doesn't already carry its own `origin`). Reset to null
    // whenever the modal closes (see setShowPaymentModal) so it can't leak
    // into a later, unrelated purchase through the same shared modal.
    paymentModalOrigin: null,
    // HORO-10635: which upsell surface opened the payment modal, sent as
    // `subscriptionSource` when checkout starts (see useGoToCheckout). Last
    // touch: every open overwrites it. Cleared on close like the origin.
    paymentModalSource: null
};

export const getPaymentModalState = (state) => state.modals.showPaymentModal;
export const getPaymentModalOrigin = (state) => state.modals.paymentModalOrigin;
export const getPaymentModalSource = (state) => state.modals.paymentModalSource;
export const getZaraUpsellModalState = (state) => state.modals.showZaraUpsellModal;
export const getZaraWelcomeModalState = (state) => state.modals.showZaraWelcomeModal;
export const getPaymentModalData = (state) => state.modals.modalData;
export const getPaymentModalType = (state) => state.modals.modalType;
export const getPaymentModalDataLoading = (state) => state.modals.loading;

// Derive whether the backend actually applied an offer from the response.
// When the OFFER_ENABLED admin config is off (or no Offer exists for the
// plan), `tryCreateOfferToUser` returns the plan with `basePrice: null` —
// previously this code flipped `isOffer: true` unconditionally, which
// caused PaymentModal to render `OfferPaymentContent` ("SPECIAL OFFER")
// even when no discounted price applied. Trust the response's basePrice
// as the signal instead.
const planResponseHasOffer = (planResponse) => !!planResponse?.basePrice;

export const tryToCreateOffer = createAsyncThunk('payment/getOffer', async (params, { rejectedWithValue, getState, dispatch }) => {
    try {
        const source = params?.source;
        const state = getState();
        // The offer is granted against one specific plan, so the same plan id
        // has to come back to the reducer — see setSubscriptionPlan.
        const plan = getFeaturedSubscriptionPlan(state);
        const userPlan = getUserPlan(state);
        // Offers ride on the trial (see FREE_TRIAL_ENABLED). With it off we skip
        // creating one, but still open the paywall — the caller is the
        // message-limit gate and the customer must see something.
        if (FREE_TRIAL_ENABLED && userPlan.isFree && plan && !plan.isOffer) {
            const data = await PaymentApi.createOfferForUserByPlan(plan.id);
            const planResponse = data.data.data.plan;
            dispatch(
                setSubscriptionPlan({
                    planId: plan.id,
                    isOffer: planResponseHasOffer(planResponse),
                    basePrice: planResponse.basePrice,
                    price: planResponse.price
                })
            );
        }
        dispatch(openPaymentModal({ source }));
    } catch (error) {
        return rejectedWithValue(error);
    }
});

export const tryToCreateOfferRedirect = createAsyncThunk(
    'payment/getOfferAfterRedirect',
    async (params, { rejectedWithValue, getState, dispatch }) => {
        try {
            // The win-back offer after leaving checkout. This whole flow is the
            // offer, so with the trial off there's nothing to show.
            if (!FREE_TRIAL_ENABLED) return undefined;

            const state = getState();
            const plan = getFeaturedSubscriptionPlan(state);
            const userPlan = getUserPlan(state);
            if (userPlan.isFree && plan) {
                const data = await PaymentApi.createOfferForUserByPlan(plan.id);
                const planResponse = data.data.data.plan;
                dispatch(setPaymentModalType(!userPlan.isTrial ? STORE_PAYMENT_MODAL_TYPE.trial : STORE_PAYMENT_MODAL_TYPE.subscription));
                dispatch(
                    setSubscriptionPlan({
                        planId: plan.id,
                        isOffer: planResponseHasOffer(planResponse),
                        basePrice: planResponse.basePrice,
                        price: planResponse.price
                    })
                );
                if (data.data.data.isCreated) {
                    dispatch(openPaymentModal({ source: 'winbackOffer' }));
                }
                return data.data.data;
            }
        } catch (error) {
            return rejectedWithValue(error);
        }
    }
);

const modalsSlice = createSlice({
    name: 'modals',
    initialState,
    reducers: {
        setShowPaymentModal(store, { payload }) {
            store.showPaymentModal = payload;
            // Closing (rather than opening) always clears the origin tag —
            // whoever opens it next with no origin of their own should never
            // inherit a stale one left over from an abandoned Zara attempt.
            if (!payload) {
                store.paymentModalOrigin = null;
                store.paymentModalSource = null;
            }
        },
        // HORO-10635: the one way to open the paywall from an upsell surface —
        // tags it with the source (and, for a locked advisor, the alias that
        // travels on as `origin`) in the same action that shows it.
        openPaymentModal(store, { payload }) {
            store.showPaymentModal = true;
            store.paymentModalSource = payload?.source ?? null;
            store.paymentModalOrigin = payload?.advisorAlias ?? null;
        },
        setPaymentModalOrigin(store, { payload }) {
            store.paymentModalOrigin = payload;
        },
        setPaymentModalType(store, { payload }) {
            store.modalType = payload;
        },
        setShowZaraUpsellModal(store, { payload }) {
            store.showZaraUpsellModal = payload;
        },
        setShowZaraWelcomeModal(store, { payload }) {
            store.showZaraWelcomeModal = payload;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(tryToCreateOffer.pending, (store) => {
                store.loading = true;
            })
            .addCase(tryToCreateOffer.fulfilled, (store) => {
                store.loading = false;
            })
            .addCase(tryToCreateOffer.rejected, (store, { payload }) => {
                store.showPaymentModal = false;
                store.modalData = initialState.modalData;
                store.error = payload?.error;
                store.loading = false;
            })
            .addCase(tryToCreateOfferRedirect.pending, (store) => {
                store.loading = true;
            })
            .addCase(tryToCreateOfferRedirect.fulfilled, (store) => {
                store.loading = false;
            })
            .addCase(tryToCreateOfferRedirect.rejected, (store, { payload }) => {
                store.showPaymentModal = false;
                store.modalData = initialState.modalData;
                store.error = payload?.error;
                store.loading = false;
            });
    }
});

export const modalsReducer = modalsSlice.reducer;

export const {
    setShowPaymentModal,
    openPaymentModal,
    setPaymentModalOrigin,
    setPaymentModalType,
    setShowZaraUpsellModal,
    setShowZaraWelcomeModal
} = modalsSlice.actions;
